/* eslint-disable max-lines */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from 'common/app-constants';
import { HvacAutoCompleteEvent } from 'common/components/hvac-autocomplete/hvac-auto-complete.component';
import { Option } from 'common/components/select/select.component';
import { ToastService } from 'common/services/toast.service';
import { bulkAssignCompaniesToGroupPayload, GroupNames, GroupOption } from 'private/app/models/account-admin-search';
import { CompanySearchResultResponse } from 'private/app/models/account-admin-search-results';
import { CompanyAuthSearchFields } from 'private/app/models/distributor-relationships.model';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { AccountPostAdminService } from 'private/app/services/account-admin/account-post-admin.service';
import { AutocompleteInputTypes, RelationshipCategory } from 'private/app/services/account-admin/company-relationship.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export type ManageGroupsTab = 'createGroup' | 'bulkMapping' | 'deleteGroup';

export interface SelectedBulkCompany {
    companyId: string;
}

@Component({
    selector: 'hvac-manage-groups-modal',
    templateUrl: './manage-groups-modal.component.html',
    styleUrls: ['./manage-groups-modal.component.scss']
})
export class ManageGroupsModalComponent implements OnInit, OnDestroy {
    @Input() isModalVisible: boolean = false;
    @Input() existingGroups: GroupOption[];
    @Output() isModalVisibleChange: EventEmitter<boolean> = new EventEmitter();
    @Output() newGroupCreated: EventEmitter<boolean> = new EventEmitter();

    public isLoading: boolean = false;
    public toastId: string = 'createGroupToast';
    public selectedTab: ManageGroupsTab = 'createGroup';
    public ngOnDestroy$ = new Subject();
    public existingGroupsForm = new UntypedFormGroup({ selectedGroup: new UntypedFormControl('') });
    public selectedCompanies: SelectedBulkCompany[] = [];
    public companyNameInputDisabled: boolean = false;
    public hvacpIdInputDisabled: boolean = false;
    public noResultsFound: boolean = false;

    public createGroupForm = new UntypedFormGroup({
        groupName: new UntypedFormControl('', [Validators.required]),
        comments: new UntypedFormControl('')
    });

    public bulkMappingForm = new UntypedFormGroup({
        selectedGroup: new UntypedFormControl('', [Validators.required]),
        companyName: new UntypedFormControl(''),
        hvacpId: new UntypedFormControl('')
    });

    public companyLookUpData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public companyLookUpDataHvacpId$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public autoComplete$: BehaviorSubject<HvacAutoCompleteEvent> = new BehaviorSubject<HvacAutoCompleteEvent>({ loading: false });
    public autoCompleteHvacId$: BehaviorSubject<HvacAutoCompleteEvent> = new BehaviorSubject<HvacAutoCompleteEvent>({ loading: false });

    private companyQuerySubject$: BehaviorSubject<string> = new BehaviorSubject('');
    private companyQuerySubjectHvacpId$: BehaviorSubject<string> = new BehaviorSubject('');
    private searchPage: Record<AutocompleteInputTypes.CompanyName | AutocompleteInputTypes.HvacpId, number> = {
        [AutocompleteInputTypes.CompanyName]: 0,
        [AutocompleteInputTypes.HvacpId]: 0
    };

    private searchTotalCount = 0;
    private readonly searchPageSize = 50;

    constructor(
        public accountAdminApiService: AccountAdminApiService,
        private readonly accountPostAdminService: AccountPostAdminService,
        private readonly toastService: ToastService,
        private readonly translate: TranslateService
    ) {}

    get selectedGroupToDelete(): GroupOption | null {
        return this.getSelectedGroupOption(this.existingGroupsForm.controls.selectedGroup.value);
    }

    get selectedBulkMappingGroup(): GroupOption | null {
        return this.getSelectedGroupOption(this.bulkMappingForm.controls.selectedGroup.value);
    }

    get canSubmitBulkMapping(): boolean {
        return this.selectedCompanies.length > 0 && Boolean(this.selectedBulkMappingGroup);
    }

    ngOnInit() {
        this.subscribeToCompanyLookups();
        this.subscribeToCompanySearchFields();
    }

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    handleTabClick(tabSelection: ManageGroupsTab) {
        this.selectedTab = tabSelection;
    }

    handleCloseModal() {
        this.createGroupForm.reset();
        this.existingGroupsForm.reset();
        this.resetBulkMappingState();
        this.selectedTab = 'createGroup';
        this.isModalVisible = false;
        this.isModalVisibleChange.next(false);
    }

    onCreateGroupSubmit(event: UntypedFormGroup) {
        if (!event.valid) {
            return;
        }

        const createGroupPayload = {
            groupName: event.value.groupName,
            comments: event.value.comments
        };
        this.isLoading = true;
        this.accountAdminApiService.createNewGroup(createGroupPayload)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe({
                next: (response: GroupNames) => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant('ACCOUNT_ADMIN.MANAGE_GROUPS.TABS.CREATE_GROUP_TAB.SUCCESS_MESSAGE', { groupName: response.groupName }),
                        theme: 'success',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                    this.newGroupCreated.emit(true);
                    this.createGroupForm.reset();
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant(error?.error),
                        theme: 'error',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                }
            });
    }

    deleteGroup() {
        const group = this.selectedGroupToDelete;
        if (!group) {
            return;
        }

        this.isLoading = true;
        this.accountAdminApiService.deleteGroup(group.value)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant('ACCOUNT_ADMIN.MANAGE_GROUPS.TABS.DELETE_GROUP.SUCCESS_MESSAGE', { groupName: group.name }),
                        theme: 'success',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                    this.existingGroupsForm.reset();
                    this.newGroupCreated.emit(true);
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant(error?.error),
                        theme: 'error',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                }
            });
    }

    onCompanyLookUp(option: Option) {
        const companyId = option?.value?.split(' - ')[0];
        if (!companyId || this.selectedCompanies.some((company) => company.companyId === companyId)) {
            this.resetCompanySearchFields();

            return;
        }

        this.selectedCompanies = [
            ...this.selectedCompanies,
            { companyId }
        ];
        this.resetCompanySearchFields();
    }

    removeSelectedCompany(companyId: string) {
        this.selectedCompanies = this.selectedCompanies.filter((company) => company.companyId !== companyId);
    }

    companyLookupWithPagination() {
        this.searchCompanies(AutocompleteInputTypes.CompanyName, this.companyQuerySubject$.value, true);
    }

    companyHvacpIdLookupWithPagination() {
        this.searchCompanies(AutocompleteInputTypes.HvacpId, this.companyQuerySubjectHvacpId$.value, true);
    }

    onBulkMappingSubmit() {
        const group = this.selectedBulkMappingGroup;
        const companyIds = this.selectedCompanies.map((company) => company.companyId);
        if (!group || !companyIds.length) {
            return;
        }

        const bulkAssignmentPayload: bulkAssignCompaniesToGroupPayload = {
            groupName: group.name,
            companyIds: companyIds
        };

        this.isLoading = true;
        this.accountAdminApiService.bulkAssignCompaniesToGroup(group.value, bulkAssignmentPayload)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant('ACCOUNT_ADMIN.MANAGE_GROUPS.TABS.BULK_COMPANY_TO_GROUP_MAPPING.SUCCESS_MESSAGE', {
                            companyCount: companyIds.length,
                            groupName: group.name
                        }),
                        theme: 'success',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                    this.resetBulkMappingState();
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.add({
                        content: this.translate.instant(error?.error),
                        theme: 'error',
                        closeable: true,
                        autoClose: true,
                        outletName: this.toastId
                    });
                }
            });
    }

    private subscribeToCompanyLookups() {
        this.companyLookUpData$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((results) => {
            this.autoComplete$.next({
                loading: false,
                loadMore: results.length < this.searchTotalCount
            });
            this.noResultsFound = results.length === 0;
        });

        this.companyLookUpDataHvacpId$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((results) => {
            this.autoCompleteHvacId$.next({
                loading: false,
                loadMore: results.length < this.searchTotalCount
            });
            this.noResultsFound = results.length === 0;
        });
    }

    private subscribeToCompanySearchFields() {
        this.bulkMappingForm.controls.companyName.valueChanges.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((companyName: string) => {
            if (companyName) {
                this.hvacpIdInputDisabled = true;
                this.companyQuerySubject$.next(companyName);
            }
            else {
                this.hvacpIdInputDisabled = false;
            }
        });

        this.bulkMappingForm.controls.hvacpId.valueChanges.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((hvacpId: string) => {
            if (hvacpId) {
                this.companyNameInputDisabled = true;
                this.companyQuerySubjectHvacpId$.next(hvacpId);
            }
            else {
                this.companyNameInputDisabled = false;
            }
        });

        this.companyQuerySubject$.pipe(
            debounceTime(AppConstants.defaultTypeAheadDebounceTime),
            takeUntil(this.ngOnDestroy$)
        ).subscribe((searchTextValue) => {
            this.searchCompanies(AutocompleteInputTypes.CompanyName, searchTextValue, false);
        });

        this.companyQuerySubjectHvacpId$.pipe(
            debounceTime(AppConstants.defaultTypeAheadDebounceTime),
            takeUntil(this.ngOnDestroy$)
        ).subscribe((searchTextValue) => {
            this.searchCompanies(AutocompleteInputTypes.HvacpId, searchTextValue, false);
        });
    }

    private searchCompanies(inputType: AutocompleteInputTypes.CompanyName | AutocompleteInputTypes.HvacpId, searchTextValue: string, isLoadMore: boolean) {
        const lookUpData$ = this.getLookUpData(inputType);
        const autoComplete$ = this.getAutoCompleteEvent(inputType);

        if (!searchTextValue) {
            lookUpData$.next([]);

            return;
        }

        // eslint-disable-next-line no-negated-condition
        if (!isLoadMore) {
            lookUpData$.next([]);
            this.searchPage[inputType] = 0;
        }
        else {
            this.searchPage[inputType] += 1;
        }

        if (inputType === AutocompleteInputTypes.HvacpId && !Number(searchTextValue)) {
            autoComplete$.next({ loading: false });

            return;
        }

        autoComplete$.next({ loading: true });
        this.accountPostAdminService.postCompanyAuthSearch(this.buildCompanySearchPayload(inputType, searchTextValue, this.searchPage[inputType]))
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe({
                next: (response: CompanySearchResultResponse) => {
                    const options = this.mapCompanySearchOptions(response);
                    this.searchTotalCount = response?.totalCount || options.length;
                    lookUpData$.next(isLoadMore ? this.mergeCompanyOptions(lookUpData$.value, options) : options);
                },
                error: () => {
                    lookUpData$.next([]);
                    autoComplete$.next({ loading: false });
                }
            });
    }

    private buildCompanySearchPayload(inputType: AutocompleteInputTypes.CompanyName | AutocompleteInputTypes.HvacpId, searchTextValue: string, page: number): CompanyAuthSearchFields {
        const isCompanyNameSearch = inputType === AutocompleteInputTypes.CompanyName;

        return {
            criteria: {
                companyTypeCodes: [RelationshipCategory.DISTRIBUTOR],
                fetchByUsersHierarchy: false,
                hvacCompanyIds: isCompanyNameSearch ? [] : [Number(searchTextValue)],
                name: isCompanyNameSearch ? `*${searchTextValue}*` : '**'
            },
            pageParams: {
                page,
                pageSize: this.searchPageSize
            },
            determineTotalCount: true
        };
    }

    private mapCompanySearchOptions(response: CompanySearchResultResponse): Option[] {
        return (response?.data || []).map((company) => ({
            name: company.companyName,
            value: company.hvacCompanyId?.toString()
        }));
    }

    private mergeCompanyOptions(existingOptions: Option[], nextOptions: Option[]): Option[] {
        return Array.from([...existingOptions, ...nextOptions].reduce((optionsById, option) => optionsById.set(option.value, option), new Map()).values());
    }

    private getLookUpData(inputType: AutocompleteInputTypes.CompanyName | AutocompleteInputTypes.HvacpId): BehaviorSubject<Option[]> {
        if (inputType === AutocompleteInputTypes.HvacpId) {
            return this.companyLookUpDataHvacpId$;
        }

        return this.companyLookUpData$;
    }

    private getAutoCompleteEvent(inputType: AutocompleteInputTypes.CompanyName | AutocompleteInputTypes.HvacpId): BehaviorSubject<HvacAutoCompleteEvent> {
        if (inputType === AutocompleteInputTypes.HvacpId) {
            return this.autoCompleteHvacId$;
        }

        return this.autoComplete$;
    }

    private resetCompanySearchFields() {
        this.bulkMappingForm.patchValue({
            companyName: '',
            hvacpId: ''
        });
        this.companyLookUpData$.next([]);
        this.companyLookUpDataHvacpId$.next([]);
        this.companyNameInputDisabled = false;
        this.hvacpIdInputDisabled = false;
    }

    private resetBulkMappingState() {
        this.bulkMappingForm.reset();
        this.selectedCompanies = [];
        this.resetCompanySearchFields();
    }

    private getSelectedGroupOption(selected: GroupOption | GroupOption[] | string | null): GroupOption | null {
        if (!selected || typeof selected === 'string') {
            return null;
        }

        const group = Array.isArray(selected) ? selected[0] : selected;

        return group?.value ? group : null;
    }
}
